/**
 * Platform Review Values - GLOBAL ON TAP
 * 
 * Configure monetary values for each review platform to calculate ROI.
 * Matches the YUL ON TAP interface with:
 * - Default Values (apply to all clients)
 * - Client-Specific Values (per-client overrides)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { platformAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import CommandBar from '../../components/CommandBar';
import { toast } from 'sonner';
import {
  DollarSign,
  Save,
  ArrowLeft,
  Loader2,
  HelpCircle,
  Building2,
} from 'lucide-react';
import { tLang } from '../../lib/langHelper';

// Review platform configurations
const REVIEW_PLATFORMS = [
  {
    key: 'google_review_value',
    name: { en: 'Google Review', fr: 'Avis Google' },
    description: { 
      en: 'Based on industry research, each Google review generates customer value',
      fr: 'Basé sur la recherche industrielle, chaque avis Google génère de la valeur client'
    },
    defaultValue: 7.5,
    icon: '🔍'
  },
  {
    key: 'tripadvisor_review_value',
    name: { en: 'Tripadvisor Review', fr: 'Avis Tripadvisor' },
    description: { 
      en: 'Tripadvisor reviews drive tourism and hospitality',
      fr: 'Les avis Tripadvisor stimulent le tourisme et l\'hôtellerie'
    },
    defaultValue: 7,
    icon: '🦉'
  },
  {
    key: 'yelp_review_value',
    name: { en: 'Yelp Review', fr: 'Avis Yelp' },
    description: { 
      en: 'Yelp reviews are crucial for local discovery',
      fr: 'Les avis Yelp sont cruciaux pour la découverte locale'
    },
    defaultValue: 6,
    icon: '⭐'
  },
  {
    key: 'trustpilot_review_value',
    name: { en: 'Trustpilot Review', fr: 'Avis Trustpilot' },
    description: { 
      en: 'Trustpilot reviews boost credibility for B2B',
      fr: 'Les avis Trustpilot renforcent la crédibilité B2B'
    },
    defaultValue: 5,
    icon: '✓'
  },
  {
    key: 'facebook_review_value',
    name: { en: 'Facebook Review', fr: 'Avis Facebook' },
    description: { 
      en: 'Facebook reviews help build social proof and trust',
      fr: 'Les avis Facebook aident à construire la preuve sociale et la confiance'
    },
    defaultValue: 4,
    icon: '📘'
  },
  {
    key: 'instagram_follow_value',
    name: { en: 'Instagram Follow', fr: 'Abonnement Instagram' },
    description: { 
      en: 'Instagram followers contribute to brand awareness',
      fr: 'Les abonnés Instagram contribuent à la notoriété de la marque'
    },
    defaultValue: 3,
    icon: '📸'
  }
];

// Value input component with help tooltip
const ValueInput = ({ platform, value, onChange, lang }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [inputValue, setInputValue] = useState(value.toString());
  
  // Sync input when value prop changes
  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);
  
  const handleChange = (e) => {
    const rawValue = e.target.value;
    
    // Allow empty string for clearing
    if (rawValue === '') {
      setInputValue('');
      return;
    }
    
    // Remove leading zeros except for decimal like "0.5"
    let cleanValue = rawValue.replace(/^0+(?=\d)/, '');
    
    // Only allow valid number characters
    if (/^[0-9]*\.?[0-9]*$/.test(cleanValue)) {
      setInputValue(cleanValue);
    }
  };
  
  const handleBlur = () => {
    // On blur, parse and update the actual value
    const numValue = parseFloat(inputValue) || 0;
    setInputValue(numValue.toString());
    onChange(numValue);
  };
  
  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <span className="text-lg">{platform.icon}</span>
        <label className="font-medium text-foreground">
          {platform.name[lang] || platform.name.en}
        </label>
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <HelpCircle className="w-4 h-4" />
        </button>
      </div>
      
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-green-500 font-medium">$</div>
        <Input
          type="text"
          inputMode="decimal"
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          className="pl-8 bg-secondary border-border text-foreground font-medium"
        />
      </div>
      
      <p className="text-sm text-muted-foreground">
        {platform.description[lang] || platform.description.en}
      </p>
    </div>
  );
};

export default function PlatformReviewValues() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('default');
  const [values, setValues] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      toast.error('Super Admin access required');
      navigate('/hotels');
      return;
    }
    fetchValues();
  }, [user, navigate]);

  const fetchValues = async () => {
    setLoading(true);
    try {
      const response = await platformAPI.getValues();
      const existingValues = response.data.values || {};
      
      // Merge with defaults
      const mergedValues = {};
      REVIEW_PLATFORMS.forEach(platform => {
        mergedValues[platform.key] = existingValues[platform.key] ?? platform.defaultValue;
      });
      
      setValues(mergedValues);
    } catch (error) {
      // Use defaults if API fails
      const defaultValues = {};
      REVIEW_PLATFORMS.forEach(platform => {
        defaultValues[platform.key] = platform.defaultValue;
      });
      setValues(defaultValues);
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (key, value) => {
    setValues(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await platformAPI.updateValues(values);
      setHasChanges(false);
      toast.success(tLang(i18n.language, 'Default values saved successfully', 'Valeurs sauvegardées', 'Default values saved successfully'));
    } catch (error) {
      toast.error('Failed to save values');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <CommandBar
        title={tLang(i18n.language, 'Platform Values', 'Valeurs Plateforme', 'Valores de plataforma')}
        subtitle={tLang(i18n.language, 'Configure monetary values for each review platform to calculate ROI', 'Configurer les valeurs monétaires pour chaque plateforme d\'avis pour calculer le ROI', 'Configure monetary values for each review platform to calculate ROI')
        }
      />

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Back button */}
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {tLang(i18n.language, 'Back to Control Center', 'Retour au Centre de Contrôle', 'Volver al centro de control')}
        </button>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-card border border-border">
            <TabsTrigger 
              value="default"
              className="data-[state=active]:bg-[hsl(217,19%,17%)] data-[state=active]:text-foreground"
            >
              {tLang(i18n.language, 'Default Values', 'Valeurs par Défaut', 'Valores predeterminados')}
            </TabsTrigger>
            <TabsTrigger 
              value="client"
              className="data-[state=active]:bg-[hsl(217,19%,17%)] data-[state=active]:text-foreground"
            >
              {tLang(i18n.language, 'Client-Specific Values', 'Valeurs Client-Spécifiques', 'Valores específicos del cliente')}
            </TabsTrigger>
          </TabsList>

          {/* Default Values Tab */}
          <TabsContent value="default" className="mt-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <DollarSign className="w-6 h-6 text-green-500" />
                  <div>
                    <CardTitle className="text-foreground">
                      {tLang(i18n.language, 'Default Platform Review Values', 'Valeurs par Défaut des Avis Plateforme', 'Default Platform Review Values')}
                    </CardTitle>
                    <CardDescription>
                      {tLang(i18n.language, 'These values apply to all clients unless custom values are set', 'Ces valeurs s\'appliquent à tous les clients sauf si des valeurs personnalisées sont définies', 'These values apply to all clients unless custom values are set')
                      }
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {REVIEW_PLATFORMS.map(platform => (
                    <ValueInput
                      key={platform.key}
                      platform={platform}
                      value={values[platform.key] ?? platform.defaultValue}
                      onChange={(val) => handleValueChange(platform.key, val)}
                      lang={i18n.language}
                    />
                  ))}
                </div>

                {/* Divider */}
                <div className="border-t border-border my-8"></div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button
                    onClick={handleSave}
                    disabled={saving || !hasChanges}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {tLang(i18n.language, 'Save Default Values', 'Sauvegarder Valeurs par Défaut', 'Save Default Values')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Client-Specific Values Tab */}
          <TabsContent value="client" className="mt-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <Building2 className="w-6 h-6 text-blue-500" />
                  <div>
                    <CardTitle className="text-foreground">
                      {tLang(i18n.language, 'Client-Specific Values', 'Valeurs Client-Spécifiques', 'Valores específicos del cliente')}
                    </CardTitle>
                    <CardDescription>
                      {tLang(i18n.language, 'Set custom values for specific clients', 'Définir des valeurs personnalisées pour des clients spécifiques', 'Set custom values for specific clients')
                      }
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Building2 className="w-16 h-16 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    {tLang(i18n.language, 'Coming Soon', 'Bientôt Disponible', 'Próximamente')}
                  </h3>
                  <p className="text-muted-foreground max-w-md">
                    {tLang(i18n.language, 'The ability to set custom values for individual clients will be available in a future update.', 'La possibilité de définir des valeurs personnalisées pour des clients individuels sera disponible dans une prochaine mise à jour.', 'The ability to set custom values for individual clients will be available in a future update.')
                    }
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
